'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, X } from 'lucide-react';
import { MOBILE_SAFE_AREA_TOP, MOBILE_SAFE_AREA_BOTTOM } from '@/components/work-items/mobileSafeArea';
import { useModalStateSafe } from '@/contexts/ModalStateContext';
import { useTheme } from '@/contexts/ThemeContext';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

interface UserDocsSheetProps {
  /** Absolute docs URL to embed (env-switched by the caller). */
  url: string;
  /** Header title — usually the link's aria label ("User guide: …"). */
  title: string;
  onClose: () => void;
}

/**
 * In-app docs viewer used on Capacitor so the `?` help icon never bounces the
 * user out to the system browser (where the hardware back button can't return).
 *
 * Renders a portal-mounted full-screen surface containing an <iframe> of the
 * docs page. The Android hardware back button is wired through the app's
 * standard modal back-handler registry (ModalStateContext) — the same Rule-0
 * path `useCapacitorMobile` consults before route navigation — so Back closes
 * the sheet and stays inside the app. Requires the docs site to allow framing
 * via CSP frame-ancestors (see next_portal/user-docs/next.config.mjs).
 */
export default function UserDocsSheet({ url, title, onClose }: UserDocsSheetProps) {
  const [mounted, setMounted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const modalState = useModalStateSafe();
  const { isDark } = useTheme();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Sync the embedded docs site with the portal's current light/dark mode —
  // the docs layout reads a ?theme=light|dark param and applies it before
  // first paint (next_portal/user-docs/src/app/layout.tsx). Recomputed if the
  // portal theme flips while the sheet is open; the iframe simply reloads.
  const themedUrl = useMemo(() => {
    const hashIndex = url.indexOf('#');
    const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
    const separator = base.includes('?') ? '&' : '?';
    // platform=mobile swaps in the page's mobileVideos:/mobileVideoUrl
    // frontmatter (app-recorded guides) instead of the portal captures —
    // see user-docs GuideVideos + the user-guide-mobile resource slot.
    return `${base}${separator}theme=${isDark ? 'dark' : 'light'}&platform=mobile${hash}`;
  }, [url, isDark]);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Hardware back (Android): register a named handler so the global
  // backButton listener routes the press here instead of navigating/minimising.
  useEffect(() => {
    if (!modalState) return;
    const handleBack = (): boolean => {
      onCloseRef.current();
      return true;
    };
    modalState.registerBackHandler('user-docs-sheet', handleBack);
    return () => modalState.unregisterBackHandler('user-docs-sheet');
  }, [modalState]);

  // Escape closes (desktop-webview dev builds; harmless on device).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lock body scroll while the sheet is open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const handleClose = useCallback(() => onCloseRef.current(), []);

  if (!mounted) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[10070] flex flex-col bg-white dark:bg-gray-900"
    >
      {/* Top safe-area spacer (notch) */}
      <div
        aria-hidden="true"
        className="flex-shrink-0 bg-white dark:bg-gray-900"
        style={{ height: MOBILE_SAFE_AREA_TOP }}
      />

      {/* Header — "Back to app" affordance mirroring modal header conventions */}
      <div className="flex flex-shrink-0 items-center gap-1 border-b border-gray-200 px-2 py-1.5 dark:border-gray-700">
        <button
          type="button"
          onClick={handleClose}
          className="flex flex-shrink-0 items-center gap-0.5 rounded-lg px-2 py-1.5 text-sm font-medium text-blue-600 transition-colors hover:bg-gray-100 dark:text-blue-400 dark:hover:bg-gray-800"
          aria-label="Back to app"
        >
          <ChevronLeft className="h-5 w-5" />
          <span>Back</span>
        </button>
        <div className="min-w-0 flex-1 truncate text-center text-sm font-semibold text-gray-900 dark:text-white">
          {title}
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="flex-shrink-0 rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      {/* Docs iframe + loading state */}
      <div className="relative min-h-0 flex-1 bg-gray-50 dark:bg-gray-950">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <BrandedLoadingSpinner
              mode="compact"
              size="sm"
              message="Loading the user guide"
              showTagline={false}
              showProgressBar={false}
            />
          </div>
        )}
        <iframe
          src={themedUrl}
          title={title}
          onLoad={() => setLoaded(true)}
          className="h-full w-full border-0"
        />
      </div>

      {/* Bottom safe-area spacer */}
      <div
        aria-hidden="true"
        className="flex-shrink-0 bg-white dark:bg-gray-900"
        style={{ height: MOBILE_SAFE_AREA_BOTTOM }}
      />
    </div>,
    document.body,
  );
}
